// biome-ignore-all lint/suspicious/useIterableCallbackReturn: TODO - address later
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import type { MetricsFilters } from "@/lib/filters";
import type {
  SalespersonMetrics as MetricsData,
  SalespersonDrilldownResult,
} from "@/types/salesperson-types";

export interface SalespersonMetricsResponse {
  person_id: string;
  metrics: MetricsData;
  touchpoint_types: string[];
}

export interface SalespersonMetricDrilldownResult {
  results: SalespersonDrilldownResult[];
  limit: number;
  offset: number;
  metric: string;
}

interface UseSalespersonMetricsParams {
  personId: string;
  touchpointTypes: string[];
  metrics: string[];
  filters?: MetricsFilters;
  dateRange?: {
    startDate?: string;
    endDate?: string;
  };
  formulas?: unknown[];
  includeBids?: boolean;
}

export function useSalespersonMetrics({
  personId,
  touchpointTypes,
  metrics,
  filters,
  dateRange,
  formulas,
  includeBids = false,
}: UseSalespersonMetricsParams) {
  return useQuery({
    queryKey: [
      "salesperson-metrics",
      personId,
      touchpointTypes,
      metrics,
      filters,
      dateRange,
      formulas,
      includeBids,
    ],
    queryFn: async () => {
      const params = new URLSearchParams();

      // Add touchpoint types
      touchpointTypes.forEach((type) =>
        params.append("touchpoint_types[]", type),
      );

      // Add metrics
      metrics.forEach((metric) => params.append("metrics[]", metric));

      // Add filters if provided
      if (filters) {
        params.append("filters", JSON.stringify(filters));
      }

      // Add date range if provided
      if (dateRange?.startDate) {
        params.append("start_date", dateRange.startDate);
      }
      if (dateRange?.endDate) {
        params.append("end_date", dateRange.endDate);
      }

      // Add formulas if provided
      if (formulas && formulas.length > 0) {
        params.append("formulas", JSON.stringify(formulas));
      }

      // Add include_bids flag
      if (includeBids) {
        params.append("include_bids", "true");
      }

      return api.get<SalespersonMetricsResponse>(
        `datahub/salespeople/${personId}/metrics/?${params.toString()}`,
      );
    },
    enabled: !!personId && touchpointTypes.length > 0 && metrics.length > 0,
  });
}

export function useSalespersonMetricDrilldown({
  personId,
  metric,
  touchpointTypes,
  filters,
  dateRange,
  limit = 1000,
  offset = 0,
  includeBids = false,
  isDrillDownEnabled = true,
}: {
  personId: string;
  metric: string;
  touchpointTypes: string[];
  filters?: MetricsFilters;
  dateRange?: {
    startDate?: string;
    endDate?: string;
  };
  limit?: number;
  offset?: number;
  includeBids?: boolean;
  isDrillDownEnabled?: boolean;
}) {
  return useQuery({
    queryKey: [
      "salesperson-metric-drilldown",
      personId,
      metric,
      touchpointTypes,
      filters,
      dateRange,
      limit,
      offset,
      includeBids,
    ],
    queryFn: async () => {
      const params = new URLSearchParams();

      // Add touchpoint types
      touchpointTypes.forEach((type) =>
        params.append("touchpoint_types[]", type),
      );

      // Add filters if provided
      if (filters) {
        params.append("filters", JSON.stringify(filters));
      }

      // Add date range if provided
      if (dateRange?.startDate) {
        params.append("start_date", dateRange.startDate);
      }
      if (dateRange?.endDate) {
        params.append("end_date", dateRange.endDate);
      }

      // Add pagination
      params.append("limit", limit.toString());
      params.append("offset", offset.toString());

      // Add include_bids flag
      if (includeBids) {
        params.append("include_bids", "true");
      }

      return api.get<SalespersonMetricDrilldownResult>(
        `datahub/salespeople/${personId}/metrics/${metric}/drilldown/?${params.toString()}`,
      );
    },
    enabled:
      !!personId &&
      !!metric &&
      touchpointTypes.length > 0 &&
      isDrillDownEnabled,
  });
}

export function useSalespersonTouchpointTypes(personId: string) {
  return useQuery({
    queryKey: ["salesperson-touchpoint-types", personId],
    queryFn: async () => {
      const response = await api.get<{ touchpoint_types: string[] }>(
        `datahub/salespeople/${personId}/touchpoint-types/`,
      );
      return response.touchpoint_types;
    },
    enabled: !!personId,
  });
}
